import React, { useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { adminApi } from "../../../../../services/Api/Admin/adminApi";
import { PartnerSummaryApi, PartnerSummaryItemApi } from "../../../../../services/Api/Partner/partnerTypes";
import AddManualPayoffModalForm from "./AddManualPayoffModalForm";
import { drawProvisionType } from "./utils/drawProvisionType";

interface Props {
	payoff: PartnerSummaryApi;
	fetchPayoff: () => Promise<void>;
	isAdminMode: boolean;
}

const PayoffTable = ({ payoff, fetchPayoff, isAdminMode }: Props) => {
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();
	const [showAddManualPayoffModal, setShowAddManualPayoffModal] = useState<boolean>(false);
	const styles: React.CSSProperties = {
		textAlign: "left",
		fontWeight: 500,
		fontSize: "16px",
		background: "#F8F9FA",
	};

	const getOrganizationLabel = (item: PartnerSummaryItemApi) => {
		if (!item?.organization?.name) return t("modules.payoff.field.manually_added.title");
		return item.organization.name;
	};

	const isAtLeastOneManuallyAddedProvision = () => {
		return !!payoff.items.find((item) => item.details.map((detail) => detail.is_added_manually));
	};

	const onRemoveTransaction = async (transactionId: number) => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});
			await adminApi.deletePayoffTransaction(Number(payoff.id), transactionId);
			await fetchPayoff();
			addSuccessFlash(t("lib:common.flash.removed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const drawPayoffTables = () => {
		return (
			<Card className="mb-3">
				<Card.Body>
					<div className="action-header">
						<h5>{t("common.word.provisions")}</h5>
						{payoff.status !== "CLOSED" && isAdminMode && (
							<Button variant="add" onClick={() => setShowAddManualPayoffModal(true)}>
								{`+${t("common.action.add", { ns: "lib" })}`}
							</Button>
						)}
					</div>
					<div className="table-responsive">
						<table className="table table-preview">
							<thead>
								<tr>
									<th>{t("lib:common.word.name")}</th>
									<th>{t("modules.payoff.field.price_paid.title")}</th>
									<th>{t("common.word.provision")}</th>
									<th>{t("common.word.billed_period")}</th>
									<th>{t("common.word.provision_value")}</th>
									{isAtLeastOneManuallyAddedProvision() && <th />}
								</tr>
							</thead>
							<tbody>
								{payoff.items.map((item, index) => {
									return (
										<>
											<tr key={index}>
												<td colSpan={4} style={styles}>
													{getOrganizationLabel(item)}
												</td>
												<td style={{ ...styles, textAlign: "right" }}>
													{FormatMoney(item.provision_value)}
												</td>
											</tr>
											{item.details.map((detail, subIndex) => {
												return (
													<tr key={`${index}_${subIndex}`}>
														<td>
															{detail.name}
															{isAdminMode && (
																<>
																	<small>
																		{(detail.client_name ||
																			detail.client_company_name ||
																			detail.client_id) && (
																			<>
																				<br />
																				{detail.client_name},{" "}
																				{detail.client_company_name},{" "}
																				{detail.client_id}
																			</>
																		)}
																		{detail.client_tax_id_no && (
																			<>
																				<br />
																				{detail.client_tax_id_no}
																			</>
																		)}
																		{detail.description && (
																			<>
																				<br />
																				Opis: {detail.description}
																			</>
																		)}
																		{(detail.subscription_id ||
																			detail.proforma_id) && (
																			<>
																				<br />
																				Subskrypcja: {
																					detail.subscription_id
																				} / {detail.proforma_id}
																			</>
																		)}
																		<br />
																	</small>

																	{detail.is_added_manually &&
																		payoff.status !== "CLOSED" &&
																		isAdminMode && (
																			<Button
																				variant="light"
																				onClick={() =>
																					onRemoveTransaction(
																						detail.partner_transaction_id
																					)
																				}
																			>
																				{t("common.action.remove", {
																					ns: "lib",
																				})}
																			</Button>
																		)}
																</>
															)}
														</td>
														<td>{FormatMoney(detail?.price_paid)}</td>
														<td>
															{detail?.provision?.provision}{" "}
															{drawProvisionType(
																detail?.provision?.type,
																detail?.price_paid?.currency
															)}
														</td>
														<td>{detail?.billed_period}</td>
														<td className="text-end">
															{FormatMoney(detail?.provision_value)}
														</td>
													</tr>
												);
											})}
										</>
									);
								})}
							</tbody>
						</table>
					</div>
				</Card.Body>
			</Card>
		);
	};

	return (
		<>
			{drawPayoffTables()}
			{showAddManualPayoffModal && (
				<AddManualPayoffModalForm
					isShown={showAddManualPayoffModal}
					payoff={payoff}
					fetchPayoff={fetchPayoff}
					onHide={() => setShowAddManualPayoffModal(false)}
				/>
			)}
		</>
	);
};
export default PayoffTable;
